/**
 * Stats table row – generic (drops and lists).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, ReactNode } from 'react';
import { CornerDownRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { GenericStatsRow } from '../shared/generic-stats.types';

import type { ColumnDef } from '../shared/constants';
import { FIRST_COLUMN_WIDTH_PX } from '../shared/constants';
import { formatMetricValue, getVoluumValueForDisplay } from '../shared';

interface StatsRowProps {
  row: GenericStatsRow;
  index: number;
  currency?: string;
  columnDefs: ColumnDef[];
  isSelected: boolean;
  onSelect: () => void;
  /** Rendered content for the first (label) column */
  firstCellContent: ReactNode;
  /** When true, adds left indent and tree connector (Gemini-style) */
  indentAsChild?: boolean;
  /** Optional responsive class for first column */
  firstColumnClassName?: string;
  /** Optional inline style for first column (when not using firstColumnClassName) */
  firstColumnStyle?: React.CSSProperties;
}

export const StatsRow: FC<StatsRowProps> = ({
  row,
  index,
  currency = 'USD',
  columnDefs,
  isSelected,
  onSelect,
  firstCellContent,
  indentAsChild = false,
  firstColumnClassName,
  firstColumnStyle,
}) => {
  const { voluum } = row;
  const profit = getVoluumValueForDisplay(voluum, 'profit');
  const profitBorderColor =
    profit != null && profit > 0
      ? 'hsl(var(--success))'
      : profit != null && profit < 0
        ? 'hsl(var(--destructive))'
        : 'hsl(var(--muted-foreground) / 0.5)';

  const selectedBg = 'color-mix(in srgb, hsl(var(--primary)) 25%, hsl(var(--card)))';

  return (
    <tr
      className={cn(
        'group transition-colors cursor-pointer',
        indentAsChild && 'border-b border-border/40 last:border-0',
        !isSelected &&
          (index % 2 === 0
            ? 'bg-card hover:bg-[hsl(var(--table-row-hover))]'
            : 'bg-[hsl(var(--table-row-zebra))] hover:bg-[hsl(var(--table-row-hover))]'),
      )}
      style={isSelected ? { background: selectedBg } : undefined}
      onClick={(e) => {
        const el = e.target as HTMLElement;
        if (el.closest('a') || el.closest('button') || el.closest('[data-no-select]')) return;
        onSelect();
      }}
    >
      <td
        className={cn(
          'sticky left-0 z-[100] py-2 align-top border-r border-border',
          indentAsChild ? 'pl-0 pr-3' : 'px-3',
          !isSelected && (index % 2 === 0 ? 'bg-card' : 'bg-[hsl(var(--table-row-zebra))]'),
          firstColumnClassName,
        )}
        style={{
          ...(firstColumnClassName
            ? {}
            : (firstColumnStyle ?? { width: FIRST_COLUMN_WIDTH_PX, minWidth: FIRST_COLUMN_WIDTH_PX })),
          background: isSelected
            ? 'color-mix(in srgb, hsl(var(--primary)) 25%, hsl(var(--card)))'
            : undefined,
          boxShadow: `inset 5px 0 0 0 ${profitBorderColor}, 4px 0 8px -2px rgba(0,0,0,0.06)`,
        }}
        title={profit != null ? `Profit: ${profit.toFixed(2)}` : undefined}
      >
        <div className="flex h-full w-full items-start">
          {indentAsChild && (
            <div className="flex shrink-0 w-[3rem] justify-end pr-2 pt-1">
              <CornerDownRight className="h-4 w-4 text-muted-foreground/40" aria-hidden />
            </div>
          )}
          <div className={cn('min-w-0 flex-1', !indentAsChild && 'pl-0')}>
            {firstCellContent}
          </div>
        </div>
      </td>
      {columnDefs.map((col) => {
        const v = getVoluumValueForDisplay(voluum, col.key);
        const formatted = formatMetricValue(v, col, currency);
        const numVal = typeof v === 'number' ? v : null;
        const hint = col.colorHint;
        const accent = 'columnAccent' in col ? (col as { columnAccent?: 'conv' | 'reg' | 'ftd' }).columnAccent : undefined;

        let colorClass = 'text-muted-foreground';
        let valueStyle: React.CSSProperties | undefined;
        if (accent && numVal != null && numVal > 0) {
          const varName =
            accent === 'conv' ? '--stat-conv-value' : accent === 'reg' ? '--stat-reg-value' : '--stat-ftd-value';
          valueStyle = { color: `hsl(var(${varName}))` };
          colorClass = 'font-medium';
        } else if (hint === 'money' && numVal != null) {
          if (numVal > 0) colorClass = 'text-success font-medium';
          else if (numVal < 0) colorClass = 'text-destructive font-medium';
        } else if (hint === 'metric' && numVal != null && numVal > 0) {
          colorClass = 'text-primary font-medium';
        } else if (hint === 'negative' && numVal != null) {
          colorClass = 'text-destructive font-medium';
        }

        return (
          <td
            key={col.key}
            className={cn('min-w-[4.5rem] px-2 py-2 text-right tabular-nums whitespace-nowrap', colorClass)}
            style={valueStyle}
          >
            {formatted}
          </td>
        );
      })}
    </tr>
  );
};
